const SWIPER_CONFIG = {
  MAX_RETRY_COUNT: 20,
  RETRY_INTERVAL_MS: 100,
  RESIZE_DEBOUNCE_MS: 250,
  DESKTOP_BREAKPOINT: 768,
  SWIPER_SELECTOR: '.swiper',
  JSON_CONFIG_SELECTOR: 'script[type="application/json"]',
};

const DEFAULT_SWIPER_OPTIONS = {
  observer: true,
  observeParents: true,
  watchSlidesProgress: true,
};

/**
 * Custom Web Component for Swiper slider initialization and management
 * Handles automatic initialization, responsive behavior, and cleanup
 *
 * @extends HTMLElement
 * @example
 * <swiper-slider>
 *   <div class="swiper">...</div>
 *   <script type="application/json">{ "slidesPerView": 3 }</script>
 * </swiper-slider>
 */

class SwiperSlider extends HTMLElement {
  #swiperInstance = null;
  #resizeHandler = null;
  #checkInterval = null;
  #retryCount = 0;

  connectedCallback() {
    this.#waitForSwiperLibrary() ? this.#setupSwiperLoadWatcher() : this.#initialize();
  }

  disconnectedCallback() {
    this.#cleanup();
  }

  #waitForSwiperLibrary() {
    return typeof Swiper === 'undefined';
  }

  #setupSwiperLoadWatcher() {
    this.#checkInterval = setInterval(() => {
      this.#retryCount++;

      if (!this.#waitForSwiperLibrary()) {
        this.#clearLoadWatcher();
        this.#initialize();
      } else if (this.#retryCount > SWIPER_CONFIG.MAX_RETRY_COUNT) {
        console.error('[SwiperSlider] Swiper library not found after maximum retry attempts.');
        this.#clearLoadWatcher();
      }
    }, SWIPER_CONFIG.RETRY_INTERVAL_MS);
  }

  #clearLoadWatcher() {
    if (this.#checkInterval) {
      clearInterval(this.#checkInterval);
      this.#checkInterval = null;
    }
  }

  #parseConfig() {
    const script = this.querySelector(SWIPER_CONFIG.JSON_CONFIG_SELECTOR);

    if (!script?.textContent) {
      return {};
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('[SwiperSlider] Failed to parse configuration:', error);
      return {};
    }
  }

  #buildSwiperOptions() {
    return {
      ...DEFAULT_SWIPER_OPTIONS,
      ...this.#parseConfig(),
    };
  }

  #customPaginationRenderer(swiper, current, total) {
    const formatNumber = (num) => String(num).padStart(2, '0');
    return `<span class="current">${formatNumber(current)}</span>/${formatNumber(total)}`;
  }

  #resolveElement(selector) {
    if (typeof selector !== 'string') {
      return selector;
    }

    const elements = this.querySelectorAll(selector);

    if (elements.length === 0) {
      return selector;
    }

    return elements.length === 1 ? elements[0] : Array.from(elements);
  }

  #configurePagination(options) {
    if (!options.pagination) {
      return;
    }

    if (options.pagination.type === 'custom' && !options.pagination.renderCustom) {
      options.pagination.renderCustom = this.#customPaginationRenderer.bind(this);
    }
    const { paginationElDesktop, paginationElMobile } = options;

    if (paginationElDesktop && paginationElMobile) {
      options.pagination.el = this.#resolveResponsivePaginationElement(paginationElDesktop, paginationElMobile);
    } else {
      options.pagination.el = this.#resolveElement(options.pagination.el);
    }
  }

  #resolveResponsivePaginationElement(desktopSelector, mobileSelector) {
    const isDesktop = window.innerWidth > SWIPER_CONFIG.DESKTOP_BREAKPOINT;
    const selector = isDesktop ? desktopSelector : mobileSelector;
    return this.querySelector(selector) || selector;
  }

  #configureNavigation(options) {
    if (!options.navigation) {
      return;
    }

    ['nextEl', 'prevEl'].forEach((key) => {
      if (options.navigation[key]) {
        options.navigation[key] = this.#resolveElement(options.navigation[key]);
      }
    });
  }

  #destroyExistingInstance() {
    if (this.#swiperInstance) {
      this.#swiperInstance.destroy(true, true);
      this.#swiperInstance = null;
    }
  }

  #initialize() {
    const container = this.querySelector(SWIPER_CONFIG.SWIPER_SELECTOR);

    if (!container) {
      console.warn('[SwiperSlider] Swiper container not found.');
      return;
    }

    this.#destroyExistingInstance();

    const options = this.#buildSwiperOptions();

    this.#configurePagination(options);
    this.#configureNavigation(options);

    this.#swiperInstance = new Swiper(container, options);

    this.#setupVideoReset();

    if (options.paginationElDesktop && options.paginationElMobile && !this.#resizeHandler) {
      this.#setupResizeHandler();
    }
  }

  #setupVideoReset() {
    this.#swiperInstance.on('slideChangeTransitionStart', () => {
      this.#swiperInstance.slides.forEach((slide) => {
        const video = slide.querySelector('video');

        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      });

      const activeSlide = this.#swiperInstance.slides[this.#swiperInstance.activeIndex];
      const activeVideo = activeSlide.querySelector('video');

      if (activeVideo) {
        activeVideo.currentTime = 0;
        activeVideo.play();
      }
    });
  }

  #setupResizeHandler() {
    let resizeTimer;

    this.#resizeHandler = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        this.#initialize();
      }, SWIPER_CONFIG.RESIZE_DEBOUNCE_MS);
    };

    window.addEventListener('resize', this.#resizeHandler);
  }

  #cleanup() {
    this.#clearLoadWatcher();

    if (this.#resizeHandler) {
      window.removeEventListener('resize', this.#resizeHandler);
      this.#resizeHandler = null;
    }

    this.#destroyExistingInstance();
  }
}

if (!customElements.get('swiper-slider')) {
  customElements.define('swiper-slider', SwiperSlider);
}
